import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Car, Users, Truck, CheckCircle, ChevronRight, ChevronLeft, Loader2, Download, LogOut,
  User, Mail, Phone, Building2, Accessibility, Ambulance, Menu, X, ArrowRight, Shield,
  TrendingUp, Handshake, MapPin, Clock, Award, Settings, FileText, BarChart3, Heart,
  Calendar, Upload, Briefcase, Star, Check, CreditCard, Building, Home as HomeIcon,
  MessageSquare, Send, Facebook, Linkedin, Instagram, Twitter, Globe, Zap, Users2,
  ClipboardList, BadgeCheck, Headphones, ChevronDown, Eye
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_nordic-logistics-hub/artifacts/gr9hbzrx_B3AB9E84-20CB-4F00-A867-405CA9D91F72.png";

const IMAGES = {
  hero: "https://customer-assets.emergentagent.com/job_nordic-logistics-hub/artifacts/scd9z187_Elderly%20care%20transport%20smiling.jpg",
  nemt: "https://images.pexels.com/photos/35831411/pexels-photo-35831411.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  mobility: "https://images.unsplash.com/photo-1732194439331-08ec61c4df4f?crop=entropy&cs=srgb&fm=jpg&q=85",
  fleet: "https://images.pexels.com/photos/33623770/pexels-photo-33623770.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  care: "https://images.unsplash.com/photo-1765896387398-1e1ae8d2eb85?crop=entropy&cs=srgb&fm=jpg&q=85",
  van: "https://images.unsplash.com/photo-1649567482458-9584de79b6c4?crop=entropy&cs=srgb&fm=jpg&q=85",
  driver: "https://images.unsplash.com/flagged/photo-1557782124-406642857a07?crop=entropy&cs=srgb&fm=jpg&q=85",
  caregiver: "https://images.pexels.com/photos/29372534/pexels-photo-29372534.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
};

const FLEET_OPTIONS = [
  { id: "1-2", label: "1-2 køretøjer", icon: Car },
  { id: "3-10", label: "3-10 køretøjer", icon: Users },
  { id: "10+", label: "10+ køretøjer", icon: Truck }
];

const EXPERIENCE_OPTIONS = [
  { id: "movia", label: "Ja, vi kører Movia/offentlige kontrakter" },
  { id: "previous", label: "Ja, vi har tidligere erfaring" },
  { id: "new", label: "Nej, vi er nye i branchen" }
];

const VEHICLE_OPTIONS = [
  { id: "personbil", label: "Personbil", icon: Car },
  { id: "liftbus", label: "Liftbus", icon: Accessibility },
  { id: "trappetjener", label: "Trappetjener", icon: Ambulance }
];

const TRANSPORT_TYPES = [
  { id: "nemt", label: "Patienttransport (NEMT)", icon: Heart },
  { id: "mobilitet", label: "Mobilitetsløsning", icon: Accessibility },
  { id: "special", label: "Specialtransport", icon: Ambulance }
];

const MOBILITY_NEEDS = [
  { id: "gaaende", label: "Gående patient" },
  { id: "koerestol", label: "Kørestolsbruger" },
  { id: "liggende", label: "Liggende transport" },
  { id: "ledsager", label: "Med ledsager" }
];

const JOB_POSITIONS = [
  "Chauffør - Patienttransport",
  "Chauffør - Liftbus",
  "Flådekoordinator",
  "Kundeservicemedarbejder",
  "Administrativ medarbejder"
];

// ==================== NAVIGATION ====================
const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showQuickInquiry, setShowQuickInquiry] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { path: "/", label: "Hjem" },
    { path: "/book-transport", label: "Book Transport" },
    { path: "/for-vognmaend", label: "Bliv Partner" },
    { path: "/medlemskab", label: "Medlemskab" },
    { path: "/karriere", label: "Karriere" },
    { path: "/om-nordivca", label: "Om os" },
    { path: "/kontakt", label: "Kontakt" }
  ];

  return (
    <>
      <header className={`header ${isScrolled ? 'scrolled' : ''}`} data-testid="header">
        <div className="max-w-7xl mx-auto px-4 lg:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img src={LOGO_URL} alt="NORDIVCA" className="h-10 md:h-12 w-auto" />
          </Link>
          <nav className="hidden lg:flex items-center gap-0.5" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link key={link.path} to={link.path}
                className={`nav-link text-sm ${location.pathname === link.path ? 'active' : ''}`}>
                {link.label}
              </Link>
            ))}
          </nav>
          <div className="hidden lg:flex items-center gap-3">
            <button onClick={() => setShowQuickInquiry(true)} className="btn-primary text-sm py-2.5 px-5" data-testid="quick-inquiry-btn">
              Hurtig Forespørgsel
            </button>
          </div>
          <button className="lg:hidden p-2" onClick={() => setIsMobileMenuOpen(true)}>
            <Menu className="w-6 h-6 text-slate-700" />
          </button>
        </div>
      </header>

      <div className={`mobile-overlay ${isMobileMenuOpen ? 'open' : ''}`} onClick={() => setIsMobileMenuOpen(false)} />
      <div className={`mobile-menu ${isMobileMenuOpen ? 'open' : ''}`}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-8">
            <img src={LOGO_URL} alt="NORDIVCA" className="h-10" />
            <button onClick={() => setIsMobileMenuOpen(false)}><X className="w-6 h-6" /></button>
          </div>
          <nav className="flex flex-col gap-1">
            {navLinks.map((link) => (
              <Link key={link.path} to={link.path} 
                className="py-3 px-4 text-base font-medium text-slate-700 hover:bg-slate-50 rounded-lg"
                onClick={() => setIsMobileMenuOpen(false)}>{link.label}</Link>
            ))}
          </nav>
          <button onClick={() => { setShowQuickInquiry(true); setIsMobileMenuOpen(false); }}
            className="btn-primary w-full mt-6">Hurtig Forespørgsel</button>
        </div>
      </div>

      <QuickInquiryModal isOpen={showQuickInquiry} onClose={() => setShowQuickInquiry(false)} />
    </>
  );
};

// Quick Inquiry Modal
const QuickInquiryModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({ full_name: '', email: '', phone: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, formData);
      setSuccess(true);
    } catch (err) { console.error(err); }
    finally { setIsSubmitting(false); }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 md:p-8">
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600">
          <X className="w-6 h-6" />
        </button>
        {success ? (
          <div className="text-center py-8">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-slate-900 mb-2">Tak for din henvendelse!</h3>
            <p className="text-slate-600">NORDIVCA har modtaget dine data, og vi behandler din sag i vores system. Du hører fra os inden for 24 timer.</p>
            <button onClick={onClose} className="btn-primary mt-6">Luk</button>
          </div>
        ) : (
          <>
            <h2 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit' }}>Hurtig Forespørgsel</h2>
            <p className="text-slate-500 mb-6">Vi svarer inden for 24 timer</p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <input type="text" required placeholder="Dit navn" className="form-input"
                value={formData.full_name} onChange={(e) => setFormData({...formData, full_name: e.target.value})} />
              <input type="email" required placeholder="Email" className="form-input"
                value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} />
              <input type="tel" required placeholder="Telefon" className="form-input"
                value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} />
              <textarea required placeholder="Din besked" rows={3} className="form-input resize-none"
                value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} />
              <button type="submit" disabled={isSubmitting} className="btn-primary w-full">
                {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Send Forespørgsel'}
              </button>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
};

// Footer
const Footer = () => (
  <footer className="footer" data-testid="footer">
    <div className="max-w-7xl mx-auto px-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 pb-12 border-b border-slate-700">
        <div className="lg:col-span-2">
          <img src={LOGO_URL} alt="NORDIVCA" className="h-12 mb-4" />
          <p className="text-slate-400 text-sm leading-relaxed max-w-sm">
            NORDIVCA DANMARK er førende inden for specialtransport og patientbefordring.
          </p>
          <div className="flex gap-3 mt-4">
            {[Facebook, Linkedin, Instagram].map((Icon, i) => (
              <a key={i} href="#" className="w-9 h-9 rounded-full bg-slate-800 flex items-center justify-center hover:bg-[#0083B0] transition-colors">
                <Icon className="w-4 h-4 text-white" />
              </a>
            ))}
          </div>
        </div>
        <div>
          <h4 className="text-white font-semibold mb-4 text-sm">Tjenester</h4>
          <div className="flex flex-col gap-2 text-sm">
            <Link to="/book-transport" className="footer-link">Book Transport</Link>
            <Link to="/for-vognmaend" className="footer-link">Bliv Partner</Link>
            <Link to="/medlemskab" className="footer-link">Medlemskab</Link>
            <Link to="/karriere" className="footer-link">Karriere</Link>
          </div>
        </div>
        <div>
          <h4 className="text-white font-semibold mb-4 text-sm">Virksomhed</h4>
          <div className="flex flex-col gap-2 text-sm">
            <Link to="/om-nordivca" className="footer-link">Om os</Link>
            <Link to="/karriere" className="footer-link">Karriere</Link>
            <Link to="/kontakt" className="footer-link">Kontakt</Link>
            <Link to="/admin" className="footer-link">Partner Login</Link>
          </div>
        </div>
        <div>
          <h4 className="text-white font-semibold mb-4 text-sm">Kontakt</h4>
          <div className="flex flex-col gap-2 text-slate-400 text-sm">
            <a href="mailto:info@nordivca.com" className="hover:text-[#00B4DB]">info@nordivca.com</a>
            <span>CVR: 44340445</span>
            <span>Vesterborgvej 3, 4953 Vesterborg</span>
          </div>
        </div>
      </div>
      <div className="py-6 flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <img src={LOGO_URL} alt="NORDIVCA" className="h-6" />
          <span className="text-slate-500 text-sm">© 2026 NORDIVCA DANMARK</span>
        </div>
        <div className="flex gap-4 text-sm">
          <a href="#" className="text-slate-500 hover:text-[#00B4DB]">Privatlivspolitik</a>
          <a href="#" className="text-slate-500 hover:text-[#00B4DB]">Vilkår</a>
        </div>
      </div>
    </div>
  </footer>
);

// HomePage
const HomePage = () => {
  const navigate = useNavigate();

  const services = [
    {
      icon: Heart,
      num: "01",
      title: "NEMT (Patienttransport)",
      desc: "Non-Emergency Medical Transportation til hospitaler, klinikker og plejecentre. Vi sikrer tryg og rettidig transport for patienter, der har brug for komfort og professionel omsorg undervejs.",
      accent: "#00B4DB"
    },
    {
      icon: Accessibility,
      num: "02",
      title: "Mobilitet & Tilgængelighed",
      desc: "Kørestolsvenlige køretøjer med hydrauliske lifte og specialudstyr. Vores flåde er bygget til at håndtere alle mobilitetsbehov med værdighed og respekt for den enkelte.",
      accent: "#0083B0"
    },
    {
      icon: BarChart3,
      num: "03",
      title: "Flådestyring & Kapacitet",
      desc: "Avanceret fleet management med GPS-tracking, ruteoptimering og realtids-dispatchering. Vi administrerer 500+ køretøjer og leverer millioner af kilometer hvert år.",
      accent: "#005F7F"
    }
  ];

  return (
    <div data-testid="home-page">
      {/* HERO - Full bleed */}
      <section className="relative w-full" style={{ minHeight: '100vh' }}>
        <img src={IMAGES.hero} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="hero-overlay" />
        <div className="relative z-10 flex items-center" style={{ minHeight: '100vh' }}>
          <div className="w-full max-w-7xl mx-auto px-6 lg:px-12 pt-28 pb-20">
            <div className="max-w-2xl">
              <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
                <span className="text-sm uppercase tracking-[0.25em] text-[#0083B0] font-bold mb-5 block">Infrastruktur for Specialtransport</span>
                <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black text-slate-900 leading-[1.05] mb-7" style={{ fontFamily: 'Outfit' }}>
                  NORDIVCA<br />DANMARK
                </h1>
                <p className="text-2xl sm:text-3xl font-bold gradient-text mb-6" style={{ fontFamily: 'Outfit' }}>Din partner i omsorg</p>
                <p className="text-base md:text-lg text-slate-700 mb-10 max-w-lg leading-relaxed">
                  Vi forbinder kvalificerede vognmænd med stabile kontrakter inden for patienttransport og specialkørsel i hele Danmark.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 mb-10">
                  <button onClick={() => navigate('/book-transport')} className="btn-primary text-lg py-4 px-8" data-testid="hero-book-btn">
                    Book Transport <ArrowRight className="w-5 h-5" />
                  </button>
                  <Link to="/for-vognmaend" className="btn-secondary text-lg py-4 px-8">Bliv Partner</Link>
                </div>
              </motion.div>
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.5 }}
                className="inline-flex items-center gap-4 bg-white/95 backdrop-blur-md rounded-2xl px-6 py-4 border border-white/50"
                style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,131,176,0.15)' }}>
                <div className="w-12 h-12 rounded-full gradient-bg flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="font-black text-slate-900 text-xl leading-none">98%+</p>
                  <p className="text-sm text-slate-500 font-medium">Kundetilfredshed</p>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="py-16 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { value: "500+", label: "Aktive Partnere" },
              { value: "50M+", label: "Kilometer årligt" },
              { value: "98%", label: "Kundetilfredshed" },
              { value: "24/7", label: "Support" }
            ].map((stat, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="stat-item">
                <div className="stat-value">{stat.value}</div>
                <div className="stat-label">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* KERNEYDELSER - Icon-based cards */}
      <section className="py-20 md:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-16 text-center">
            <h2 className="text-3xl md:text-5xl font-black text-slate-900 mb-4" style={{ fontFamily: 'Outfit' }}>Vores Kerneydelser</h2>
            <p className="text-slate-500 max-w-2xl mx-auto text-base md:text-lg">Komplette transportløsninger designet til sundhedssektorens behov</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.12, duration: 0.5 }}
                className="relative bg-white rounded-2xl p-8 md:p-10 border border-slate-100 hover:border-[#00B4DB]/30 transition-all duration-300 group"
                style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.04)' }}>
                <div className="absolute top-8 right-8 text-6xl font-black text-slate-100 select-none" style={{ fontFamily: 'Outfit' }}>{s.num}</div>
                <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-6 transition-colors duration-300"
                  style={{ background: `linear-gradient(135deg, ${s.accent}15, ${s.accent}08)` }}>
                  <s.icon className="w-7 h-7" style={{ color: s.accent }} />
                </div>
                <h3 className="text-xl md:text-2xl font-black text-slate-900 mb-3 relative z-10" style={{ fontFamily: 'Outfit' }}>{s.title}</h3>
                <p className="text-slate-600 leading-relaxed relative z-10">{s.desc}</p>
                <div className="mt-6 h-1 w-12 rounded-full group-hover:w-20 transition-all duration-300" style={{ background: `linear-gradient(to right, ${s.accent}, ${s.accent}80)` }} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 gradient-bg">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-5xl font-black text-white mb-4" style={{ fontFamily: 'Outfit' }}>Klar til at komme i gang?</h2>
          <p className="text-white/80 mb-10 text-lg">Tilmeld dig som partner og bliv en del af vores professionelle netværk i dag.</p>
          <button onClick={() => navigate('/for-vognmaend')} className="bg-white text-[#0083B0] font-bold py-4 px-10 rounded-lg hover:bg-slate-50 transition-all text-lg" style={{ boxShadow: '0 8px 30px rgba(0,0,0,0.15)' }}>Bliv Partner</button>
        </div>
      </section>
    </div>
  );
};

// BookTransportPage
const BookTransportPage = () => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    transport_type: '', mobility_needs: [], from_address: '', to_address: '',
    date: '', time: '', full_name: '', email: '', phone: '', notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const canProceed = () => {
    switch(step) {
      case 1: return !!formData.transport_type;
      case 2: return formData.mobility_needs.length > 0;
      case 3: return formData.from_address && formData.to_address && formData.date && formData.time;
      case 4: return formData.full_name && formData.email && formData.phone;
      default: return false;
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, formData);
      setSuccess(true);
    } catch (err) { console.error(err); }
    finally { setIsSubmitting(false); }
  };

  if (success) {
    return (
      <div className="pt-24 pb-16 min-h-screen bg-gradient-to-br from-white via-slate-50 to-blue-50">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="w-24 h-24 rounded-full gradient-bg flex items-center justify-center mx-auto mb-8">
            <CheckCircle className="w-12 h-12 text-white" />
          </motion.div>
          <h1 className="text-3xl font-bold text-slate-900 mb-4">Tak for din forespørgsel!</h1>
          <p className="text-slate-600 mb-8">NORDIVCA har modtaget dine data, og vi behandler din sag i vores system. Du hører fra os inden for 24 timer.</p>
          <Link to="/" className="btn-primary">Tilbage til forsiden</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20" data-testid="book-transport-page">
      <section className="py-12 bg-gradient-to-br from-white via-slate-50 to-blue-50 min-h-screen">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit' }}>Book Transport</h1>
            <p className="text-slate-600">Udfyld formularen og vi vender tilbage med et tilbud</p>
          </div>

          <div className="flex items-center justify-center gap-2 mb-8">
            {[1,2,3,4].map((s) => (
              <div key={s} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm ${step >= s ? 'gradient-bg text-white' : 'bg-slate-200 text-slate-500'}`}>{s}</div>
                {s < 4 && <div className={`w-12 h-1 ${step > s ? 'gradient-bg' : 'bg-slate-200'}`} />}
              </div>
            ))}
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-6 md:p-10">
            {step === 1 && (
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-6">Vælg kørselstype</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {TRANSPORT_TYPES.map((t) => (
                    <div key={t.id} onClick={() => setFormData({...formData, transport_type: t.id})}
                      className={`p-6 rounded-xl border-2 cursor-pointer transition-all text-center ${formData.transport_type === t.id ? 'border-[#00B4DB] bg-[#00B4DB]/5' : 'border-slate-200 hover:border-slate-300'}`}>
                      <t.icon className={`w-10 h-10 mx-auto mb-3 ${formData.transport_type === t.id ? 'text-[#0083B0]' : 'text-slate-400'}`} />
                      <p className="font-semibold text-slate-800">{t.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {step === 2 && (
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-6">Mobilitetsbehov</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {MOBILITY_NEEDS.map((n) => {
                    const selected = formData.mobility_needs.includes(n.id);
                    return (
                      <div key={n.id} onClick={() => {
                        const needs = selected ? formData.mobility_needs.filter(x => x !== n.id) : [...formData.mobility_needs, n.id];
                        setFormData({...formData, mobility_needs: needs});
                      }} className={`p-4 rounded-xl border-2 cursor-pointer transition-all flex items-center gap-4 ${selected ? 'border-[#00B4DB] bg-[#00B4DB]/5' : 'border-slate-200 hover:border-slate-300'}`}>
                        <div className={`w-6 h-6 rounded flex items-center justify-center ${selected ? 'gradient-bg' : 'border-2 border-slate-300'}`}>
                          {selected && <Check className="w-4 h-4 text-white" />}
                        </div>
                        <span className="font-medium text-slate-700">{n.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {step === 3 && (
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-6">Logistik</h2>
                <div className="space-y-4">
                  <div>
                    <label className="form-label"><MapPin className="inline w-4 h-4 mr-2" />Fra adresse</label>
                    <input type="text" className="form-input" placeholder="Indtast afhentningsadresse"
                      value={formData.from_address} onChange={(e) => setFormData({...formData, from_address: e.target.value})} />
                  </div>
                  <div>
                    <label className="form-label"><MapPin className="inline w-4 h-4 mr-2" />Til adresse</label>
                    <input type="text" className="form-input" placeholder="Indtast destination"
                      value={formData.to_address} onChange={(e) => setFormData({...formData, to_address: e.target.value})} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="form-label"><Calendar className="inline w-4 h-4 mr-2" />Dato</label>
                      <input type="date" className="form-input" value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} />
                    </div>
                    <div>
                      <label className="form-label"><Clock className="inline w-4 h-4 mr-2" />Tidspunkt</label>
                      <input type="time" className="form-input" value={formData.time} onChange={(e) => setFormData({...formData, time: e.target.value})} />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {step === 4 && (
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-6">Kontaktoplysninger</h2>
                <div className="space-y-4">
                  <div>
                    <label className="form-label"><User className="inline w-4 h-4 mr-2" />Fulde navn</label>
                    <input type="text" className="form-input" placeholder="Dit navn" value={formData.full_name} onChange={(e) => setFormData({...formData, full_name: e.target.value})} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="form-label"><Mail className="inline w-4 h-4 mr-2" />Email</label>
                      <input type="email" className="form-input" placeholder="din@email.dk" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} />
                    </div>
                    <div>
                      <label className="form-label"><Phone className="inline w-4 h-4 mr-2" />Telefon</label>
                      <input type="tel" className="form-input" placeholder="+45 12 34 56 78" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} />
                    </div>
                  </div>
                  <div>
                    <label className="form-label">Bemærkninger (valgfrit)</label>
                    <textarea className="form-input resize-none" rows={3} placeholder="Særlige behov" value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} />
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-between items-center mt-8 pt-6 border-t border-slate-100">
              {step > 1 ? (
                <button onClick={() => setStep(step - 1)} className="flex items-center gap-2 text-slate-500 font-medium hover:text-slate-900">
                  <ChevronLeft className="w-5 h-5" /> Tilbage
                </button>
              ) : <div />}
              {step < 4 ? (
                <button onClick={() => setStep(step + 1)} disabled={!canProceed()} className="btn-primary">Næste <ChevronRight className="w-5 h-5" /></button>
              ) : (
                <button onClick={handleSubmit} disabled={!canProceed() || isSubmitting} className="btn-primary">
                  {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Send Forespørgsel'}
                </button>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// MedlemskabPage
const MedlemskabPage = () => {
  const [selectedTier, setSelectedTier] = useState(null);
  const [formData, setFormData] = useState({ tier: '', organization_name: '', contact_name: '', email: '', phone: '', organization_type: '', expected_volume: '', notes: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const tiers = [
    { id: 'essential', name: 'Essential', subtitle: 'Private', price: 'Fast lavt gebyr', features: ['Certificeret tryghed', 'Standard bookings', 'Email support', 'Månedlig fakturering'] },
    { id: 'premium', name: 'Premium', subtitle: 'Familier', price: 'Prioritet', features: ['Priority dispatch', 'Fast chauffør', '24/7 support', 'Rabat på alle ture'], popular: true },
    { id: 'clinical', name: 'Clinical', subtitle: 'Private Hospitaler', price: 'Enterprise', features: ['SLA-garanti', 'Flåde-integration', 'Fuld compliance', 'Dedikeret account manager'] }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/subscriptions`, { ...formData, tier: selectedTier });
      setSuccess(true);
    } catch (err) { console.error(err); }
    finally { setIsSubmitting(false); }
  };

  return (
    <div className="pt-20" data-testid="medlemskab-page">
      <section className="py-16 bg-gradient-to-br from-white via-slate-50 to-blue-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit' }}>Medlemskab & Fordele</h1>
            <p className="text-slate-600 max-w-2xl mx-auto">Vælg det medlemskab der passer til dine behov</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
            {tiers.map((tier) => (
              <div key={tier.id} className={`relative bg-white rounded-2xl p-6 border-2 transition-all cursor-pointer
                ${selectedTier === tier.id ? 'border-[#00B4DB] shadow-lg' : 'border-slate-200 hover:border-slate-300'}
                ${tier.popular ? 'ring-2 ring-[#00B4DB]' : ''}`}
                onClick={() => setSelectedTier(tier.id)}>
                {tier.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-[#00B4DB] to-[#0083B0] text-white text-xs font-bold px-4 py-1 rounded-full">
                    Populær
                  </div>
                )}
                <h3 className="text-2xl font-bold text-slate-900">{tier.name}</h3>
                <p className="text-sm text-[#0083B0] mb-4">{tier.subtitle}</p>
                <p className="text-lg font-semibold text-slate-700 mb-6">{tier.price}</p>
                <ul className="space-y-3">
                  {tier.features.map((f, i) => (
                    <li key={i} className="flex items-center gap-2 text-slate-600">
                      <Check className="w-5 h-5 text-[#00B4DB]" />{f}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {selectedTier && !success && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl shadow-xl p-8 max-w-2xl mx-auto">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">Kontakt os for tilbud</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <input type="text" required placeholder="Organisation/Virksomhed" className="form-input"
                  value={formData.organization_name} onChange={(e) => setFormData({...formData, organization_name: e.target.value})} />
                <input type="text" required placeholder="Kontaktperson" className="form-input"
                  value={formData.contact_name} onChange={(e) => setFormData({...formData, contact_name: e.target.value})} />
                <div className="grid grid-cols-2 gap-4">
                  <input type="email" required placeholder="Email" className="form-input"
                    value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} />
                  <input type="tel" required placeholder="Telefon" className="form-input"
                    value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} />
                </div>
                <select className="form-input" value={formData.organization_type} onChange={(e) => setFormData({...formData, organization_type: e.target.value})}>
                  <option value="">Vælg organisationstype</option>
                  <option value="private">Privat</option>
                  <option value="hospital">Hospital</option>
                  <option value="kommune">Kommune</option>
                  <option value="other">Andet</option>
                </select>
                <input type="text" placeholder="Forventet månedligt volumen (valgfrit)" className="form-input"
                  value={formData.expected_volume} onChange={(e) => setFormData({...formData, expected_volume: e.target.value})} />
                <button type="submit" disabled={isSubmitting} className="btn-primary w-full">
                  {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Send Forespørgsel'}
                </button>
              </form>
            </motion.div>
          )}

          {success && (
            <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md mx-auto text-center">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Tak for din henvendelse!</h2>
              <p className="text-slate-600">NORDIVCA har modtaget dine data, og vi behandler din sag i vores system. Du hører fra os inden for 24 timer.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

// ForVognmaendPage with Wizard
const ForVognmaendPage = () => {
  const [step, setStep] = useState(0); // 0 = landing, 1-4 = wizard steps
  const [formData, setFormData] = useState({ fleet_size: '', experience: '', vehicle_types: [], full_name: '', cvr_number: '', email: '', phone: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (window.location.hash === '#wizard') {
      setStep(1);
      setTimeout(() => document.getElementById('wizard')?.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  }, []);

  const canProceed = () => {
    switch(step) {
      case 1: return !!formData.fleet_size;
      case 2: return !!formData.experience;
      case 3: return formData.vehicle_types.length > 0;
      case 4: return formData.full_name && formData.cvr_number && formData.email && formData.phone;
      default: return false;
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        ...formData,
        fleet_size: FLEET_OPTIONS.find(f => f.id === formData.fleet_size)?.label || formData.fleet_size,
        experience: EXPERIENCE_OPTIONS.find(e => e.id === formData.experience)?.label || formData.experience,
        vehicle_types: formData.vehicle_types.map(id => VEHICLE_OPTIONS.find(v => v.id === id)?.label || id)
      });
      setSuccess(true);
    } catch (err) { console.error(err); }
    finally { setIsSubmitting(false); }
  };

  const stepTitles = ['', 'Angiv jeres flådekapacitet', 'Jeres erfaring inden for transport', 'Kvalitets- og sikkerhedsvalidering', 'Afsluttende validering: Jeres virksomhedsprofil'];

  const wizardContent = () => {
    if (success) {
      return (
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="p-10 md:p-16 text-center">
          <div className="w-20 h-20 rounded-full gradient-bg flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-2xl md:text-3xl font-black text-slate-900 mb-4" style={{ fontFamily: 'Outfit' }}>Velkommen til NORDIVCA</h2>
          <p className="text-slate-600 max-w-md mx-auto leading-relaxed">Tak for jeres interesse. Vores Partner-team gennemgår jeres profil og kontakter jer inden for 48 timer for en validerings-samtale.</p>
        </motion.div>
      );
    }

    return (
      <>
        <div className="bg-slate-900 p-6 md:p-8">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-[#00B4DB] text-xs font-bold uppercase tracking-[0.2em]">Partnerskabsvalidering</p>
              <h3 className="text-white text-xl md:text-2xl font-black mt-1" style={{ fontFamily: 'Outfit' }}>Trin {step} af 4</h3>
            </div>
            <div className="text-[#00B4DB] text-3xl font-black">{step * 25}%</div>
          </div>
          <div className="flex gap-2 mt-5">
            {[1,2,3,4].map((s) => (
              <div key={s} className={`flex-1 h-1 rounded-full transition-all duration-500 ${step >= s ? 'gradient-bg' : 'bg-white/15'}`} />
            ))}
          </div>
        </div>

        <div className="p-6 md:p-10">
          <AnimatePresence mode="wait">
            <motion.div key={step} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.3 }}>
              <h2 className="text-xl md:text-2xl font-black text-slate-900 mb-6" style={{ fontFamily: 'Outfit' }}>{stepTitles[step]}</h2>

              {step === 1 && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {FLEET_OPTIONS.map((opt) => {
                    const Icon = opt.icon;
                    const selected = formData.fleet_size === opt.id;
                    return (
                      <div key={opt.id} onClick={() => setFormData({...formData, fleet_size: opt.id})} data-testid={`fleet-${opt.id}`}
                        className={`p-6 rounded-2xl border-2 cursor-pointer text-center transition-all duration-200
                          ${selected ? 'border-[#00B4DB] bg-gradient-to-b from-[#00B4DB]/8 to-transparent shadow-[0_0_0_1px_rgba(0,180,219,0.3),0_8px_24px_rgba(0,131,176,0.12)]' : 'border-slate-200 hover:border-slate-300 hover:shadow-md'}`}>
                        <Icon className={`w-10 h-10 mx-auto mb-3 transition-colors ${selected ? 'text-[#0083B0]' : 'text-slate-400'}`} />
                        <p className="font-bold text-slate-800">{opt.label}</p>
                      </div>
                    );
                  })}
                </div>
              )}

              {step === 2 && (
                <div className="space-y-3">
                  {EXPERIENCE_OPTIONS.map((opt) => {
                    const selected = formData.experience === opt.id;
                    return (
                      <div key={opt.id} onClick={() => setFormData({...formData, experience: opt.id})} data-testid={`exp-${opt.id}`}
                        className={`p-5 rounded-2xl border-2 cursor-pointer flex items-center gap-4 transition-all duration-200
                          ${selected ? 'border-[#00B4DB] bg-gradient-to-r from-[#00B4DB]/8 to-transparent shadow-[0_0_0_1px_rgba(0,180,219,0.3),0_4px_16px_rgba(0,131,176,0.1)]' : 'border-slate-200 hover:border-slate-300'}`}>
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${selected ? 'border-[#00B4DB] gradient-bg' : 'border-slate-300'}`}>
                          {selected && <div className="w-2 h-2 bg-white rounded-full" />}
                        </div>
                        <span className="font-semibold text-slate-700">{opt.label}</span>
                      </div>
                    );
                  })}
                </div>
              )}

              {step === 3 && (
                <div className="space-y-3">
                  {VEHICLE_OPTIONS.map((opt) => {
                    const Icon = opt.icon;
                    const selected = formData.vehicle_types.includes(opt.id);
                    return (
                      <div key={opt.id} onClick={() => {
                        const types = selected ? formData.vehicle_types.filter(x => x !== opt.id) : [...formData.vehicle_types, opt.id];
                        setFormData({...formData, vehicle_types: types});
                      }} data-testid={`vehicle-${opt.id}`}
                        className={`p-5 rounded-2xl border-2 cursor-pointer flex items-center gap-4 transition-all duration-200
                          ${selected ? 'border-[#00B4DB] bg-gradient-to-r from-[#00B4DB]/8 to-transparent shadow-[0_0_0_1px_rgba(0,180,219,0.3),0_4px_16px_rgba(0,131,176,0.1)]' : 'border-slate-200 hover:border-slate-300'}`}>
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${selected ? 'gradient-bg' : 'border-2 border-slate-300'}`}>
                          {selected && <Check className="w-5 h-5 text-white" />}
                        </div>
                        <Icon className={`w-6 h-6 ${selected ? 'text-[#0083B0]' : 'text-slate-400'}`} />
                        <span className="font-semibold text-slate-700">{opt.label}</span>
                      </div>
                    );
                  })}
                </div>
              )}

              {step === 4 && (
                <div className="space-y-5">
                  <div>
                    <label className="form-label">Virksomhedens kontaktperson</label>
                    <input type="text" placeholder="Fulde navn" className="form-input" value={formData.full_name} onChange={(e) => setFormData({...formData, full_name: e.target.value})} data-testid="partner-name" />
                  </div>
                  <div>
                    <label className="form-label">CVR-nummer</label>
                    <input type="text" placeholder="DK-12345678" className="form-input" value={formData.cvr_number} onChange={(e) => setFormData({...formData, cvr_number: e.target.value})} data-testid="partner-cvr" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="form-label">Email</label>
                      <input type="email" placeholder="din@virksomhed.dk" className="form-input" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} data-testid="partner-email" />
                    </div>
                    <div>
                      <label className="form-label">Telefon</label>
                      <input type="tel" placeholder="+45 XX XX XX XX" className="form-input" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} data-testid="partner-phone" />
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          <div className="flex justify-between items-center mt-8 pt-6 border-t border-slate-100">
            {step > 1 ? (
              <button onClick={() => setStep(step - 1)} className="flex items-center gap-2 text-slate-500 font-semibold hover:text-slate-900 transition-colors">
                <ChevronLeft className="w-5 h-5" /> Tilbage
              </button>
            ) : <div />}
            {step < 4 ? (
              <button onClick={() => setStep(step + 1)} disabled={!canProceed()} className="btn-primary">Næste <ChevronRight className="w-5 h-5" /></button>
            ) : (
              <button onClick={handleSubmit} disabled={!canProceed() || isSubmitting} className="btn-primary px-8" data-testid="submit-partner-btn">
                {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Indsend partnerskabsprofil til validering'}
              </button>
            )}
          </div>

          {/* GDPR */}
          <div className="mt-6 flex items-center gap-2 text-xs text-slate-400">
            <Shield className="w-4 h-4 flex-shrink-0" />
            <span>Dine data behandles fortroligt i overensstemmelse med GDPR. Vi deler aldrig dine oplysninger med tredjepart.</span>
          </div>
        </div>
      </>
    );
  };

  return (
    <div data-testid="for-vognmaend-page">
      {/* HERO */}
      <section className="relative w-full bg-slate-900 overflow-hidden" style={{ minHeight: step === 0 ? '100vh' : 'auto' }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-[800px] h-[800px] rounded-full" style={{ background: 'radial-gradient(circle, #00B4DB 0%, transparent 70%)' }} />
          <div className="absolute bottom-0 left-0 w-[600px] h-[600px] rounded-full" style={{ background: 'radial-gradient(circle, #0083B0 0%, transparent 70%)' }} />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 pt-32 pb-20">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-3xl">
            <span className="text-xs uppercase tracking-[0.3em] text-[#00B4DB] font-bold mb-6 block">Strategisk Partnerskab</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-[1.08] mb-6" style={{ fontFamily: 'Outfit' }}>
              En stærkere fremtid gennem strategiske partnerskaber
            </h1>
            <p className="text-lg md:text-xl text-slate-300 mb-10 max-w-2xl leading-relaxed">
              NORDIVCA er vognmandens alliancepartner. Vi sikrer økonomisk ligeværd og priser, der afspejler jeres kvalitet, så I kan drive en sund og bæredygtig forretning.
            </p>
            {step === 0 && (
              <motion.button initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
                onClick={() => { setStep(1); setTimeout(() => document.getElementById('wizard')?.scrollIntoView({ behavior: 'smooth' }), 100); }}
                className="bg-[#00B4DB] text-white font-bold py-4 px-10 rounded-lg hover:bg-[#0083B0] transition-all text-lg inline-flex items-center gap-3"
                style={{ boxShadow: '0 4px 20px rgba(0,180,219,0.4)' }} data-testid="start-wizard-btn">
                Start partnerskabsvalidering <ArrowRight className="w-5 h-5" />
              </motion.button>
            )}
          </motion.div>
        </div>
      </section>

      {/* VALUE PROPS */}
      {step === 0 && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
              {[
                {
                  icon: Shield,
                  title: "Ligeværdig Konkurrence",
                  desc: "Vi modvirker usundt prispres. Vi sikrer, at udbudspriser giver plads til overskud og ordentlige medarbejdervilkår."
                },
                {
                  icon: CreditCard,
                  title: "Likviditets-sikkerhed",
                  desc: "Vi forstår jeres drift. NORDIVCA tilbyder gennemsigtig og punktlig afregning, der styrker jeres virksomheds fundament."
                },
                {
                  icon: Award,
                  title: "Kvalitets-fokus",
                  desc: "Vi forener de bedste vognmænd i et netværk, hvor professionalisme belønnes frem for laveste pris."
                }
              ].map((vp, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.15 }}
                  className="relative">
                  <div className="w-14 h-14 rounded-xl gradient-bg flex items-center justify-center mb-5">
                    <vp.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl md:text-2xl font-black text-slate-900 mb-3" style={{ fontFamily: 'Outfit' }}>{vp.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{vp.desc}</p>
                </motion.div>
              ))}
            </div>
            <div className="text-center mt-16">
              <button onClick={() => { setStep(1); setTimeout(() => document.getElementById('wizard')?.scrollIntoView({ behavior: 'smooth' }), 100); }}
                className="btn-primary text-lg py-4 px-10" data-testid="start-wizard-btn-2">
                Start din ansøgning <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </section>
      )}

      {/* WIZARD */}
      {step > 0 && (
        <section id="wizard" className="py-16 md:py-20 bg-slate-50">
          <div className="max-w-2xl mx-auto px-6">
            <div className="bg-white rounded-3xl overflow-hidden border border-slate-100" style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.08), 0 0 0 1px rgba(0,0,0,0.03)' }}>
              {wizardContent()}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

// KarrierePage
const KarrierePage = () => {
  const [formData, setFormData] = useState({ full_name: '', email: '', phone: '', position: '' });
  const [files, setFiles] = useState({ cv: null, cover_letter: null, documents: null });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('full_name', formData.full_name);
      fd.append('email', formData.email);
      fd.append('phone', formData.phone);
      fd.append('position', formData.position);
      if (files.cv) fd.append('cv', files.cv);
      if (files.cover_letter) fd.append('cover_letter', files.cover_letter);
      if (files.documents) fd.append('documents', files.documents);

      await axios.post(`${API}/jobs/apply`, fd, { headers: { 'Content-Type': 'multipart/form-data' }});
      setSuccess(true);
    } catch (err) { console.error(err); }
    finally { setIsSubmitting(false); }
  };

  return (
    <div className="pt-20" data-testid="karriere-page">
      <section className="py-16 bg-gradient-to-br from-white via-slate-50 to-blue-50 min-h-screen">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit' }}>Karriere hos NORDIVCA</h1>
            <p className="text-slate-600 max-w-2xl mx-auto">Bliv en del af vores team og gør en forskel i menneskers liv</p>
          </div>

          {success ? (
            <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Tak for din ansøgning!</h2>
              <p className="text-slate-600">NORDIVCA har modtaget dine data, og vi behandler din sag i vores system. Du hører fra os inden for 24 timer.</p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">Send din ansøgning</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="form-label"><User className="inline w-4 h-4 mr-2" />Fulde navn *</label>
                    <input type="text" required className="form-input" value={formData.full_name} onChange={(e) => setFormData({...formData, full_name: e.target.value})} />
                  </div>
                  <div>
                    <label className="form-label"><Mail className="inline w-4 h-4 mr-2" />Email *</label>
                    <input type="email" required className="form-input" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="form-label"><Phone className="inline w-4 h-4 mr-2" />Telefon *</label>
                    <input type="tel" required className="form-input" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} />
                  </div>
                  <div>
                    <label className="form-label"><Briefcase className="inline w-4 h-4 mr-2" />Stilling ønsket *</label>
                    <select required className="form-input" value={formData.position} onChange={(e) => setFormData({...formData, position: e.target.value})}>
                      <option value="">Vælg stilling</option>
                      {JOB_POSITIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="form-label"><Upload className="inline w-4 h-4 mr-2" />CV (PDF)</label>
                    <input type="file" accept=".pdf,.doc,.docx" className="form-input" onChange={(e) => setFiles({...files, cv: e.target.files[0]})} />
                  </div>
                  <div>
                    <label className="form-label"><FileText className="inline w-4 h-4 mr-2" />Ansøgning (PDF)</label>
                    <input type="file" accept=".pdf,.doc,.docx" className="form-input" onChange={(e) => setFiles({...files, cover_letter: e.target.files[0]})} />
                  </div>
                  <div>
                    <label className="form-label"><FileText className="inline w-4 h-4 mr-2" />Dokumenter (Kørekort/Straffeattest)</label>
                    <input type="file" accept=".pdf,.doc,.docx,.jpg,.png" className="form-input" onChange={(e) => setFiles({...files, documents: e.target.files[0]})} />
                  </div>
                </div>
                <button type="submit" disabled={isSubmitting} className="btn-primary w-full">
                  {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Send Ansøgning'}
                </button>
              </form>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

// OmNordivca & Kontakt Pages
const OmNordivcaPage = () => (
  <div className="pt-20" data-testid="om-nordivca-page">
    <section className="py-16">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit' }}>Om NORDIVCA</h1>
          <p className="text-xl text-slate-600">Infrastruktur for fremtidens transport</p>
        </div>
        <div className="prose prose-lg max-w-none text-slate-600">
          <p className="text-xl leading-relaxed mb-8">
            NORDIVCA DANMARK er førende infrastrukturudbyder inden for specialtransport. 
            Vi fungerer som bindeled mellem professionelle vognmænd og sundhedssektoren.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 my-12">
            <div className="bg-slate-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-slate-900 mb-3">Vores Mission</h3>
              <p className="text-slate-600">At skabe Danmarks mest pålidelige netværk af transportpartnere med fokus på kvalitet, sikkerhed og bæredygtighed.</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-slate-900 mb-3">Vores Vision</h3>
              <p className="text-slate-600">At være førstevalget for både vognmænd og sundhedsudbydere inden for specialiseret patienttransport.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
);

const KontaktPage = () => {
  const [formData, setFormData] = useState({ full_name: '', email: '', phone: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, { ...formData, inquiry_type: 'contact' });
      setSubmitted(true);
    } catch (err) { console.error(err); }
    finally { setIsSubmitting(false); }
  };

  return (
    <div className="pt-20" data-testid="kontakt-page">
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit' }}>Kontakt</h1>
            <p className="text-slate-600">Vi er klar til at hjælpe dig</p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-6">Kontaktinformation</h2>
              <div className="space-y-6">
                {[
                  { icon: Mail, title: 'Email', value: 'info@nordivca.com' },
                  { icon: Building2, title: 'CVR', value: '44340445' },
                  { icon: MapPin, title: 'Adresse', value: 'Vesterborgvej 3, 4953 Vesterborg' }
                ].map((c, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg gradient-bg flex items-center justify-center flex-shrink-0">
                      <c.icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">{c.title}</h3>
                      <p className="text-slate-600">{c.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-lg">
              {submitted ? (
                <div className="text-center py-8">
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-slate-900 mb-2">Tak for din henvendelse!</h3>
                  <p className="text-slate-600">NORDIVCA har modtaget dine data, og vi behandler din sag i vores system. Du hører fra os inden for 24 timer.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <input type="text" required placeholder="Navn" className="form-input" value={formData.full_name} onChange={(e) => setFormData({...formData, full_name: e.target.value})} />
                  <input type="email" required placeholder="Email" className="form-input" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} />
                  <input type="tel" placeholder="Telefon" className="form-input" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} />
                  <textarea required rows={4} placeholder="Din besked" className="form-input resize-none" value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} />
                  <button type="submit" disabled={isSubmitting} className="btn-primary w-full">
                    {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Send Besked'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// AdminPage
const AdminPage = () => {
  const [auth, setAuth] = useState(() => {
    const token = localStorage.getItem('nordivca_admin_token');
    return token ? { token } : null;
  });
  const [activeTab, setActiveTab] = useState('partners');
  const [stats, setStats] = useState(null);
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [validating, setValidating] = useState(true);

  const authHeaders = auth ? { headers: { Authorization: `Bearer ${auth.token}` } } : {};

  useEffect(() => {
    const validateToken = async () => {
      const token = localStorage.getItem('nordivca_admin_token');
      if (!token) { setValidating(false); return; }
      try {
        await axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } });
        setValidating(false);
      } catch {
        localStorage.removeItem('nordivca_admin_token');
        setAuth(null);
        setValidating(false);
      }
    };
    validateToken();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    if (!auth) return;
    setIsLoading(true);
    try {
      const [statsRes, dataRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, authHeaders),
        axios.get(`${API}/admin/${activeTab}`, authHeaders)
      ]);
      setStats(statsRes.data);
      setData(dataRes.data);
    } catch (err) {
      if (err.response?.status === 401) { localStorage.removeItem('nordivca_admin_token'); setAuth(null); }
      console.error(err);
    }
    finally { setIsLoading(false); }
  };

  useEffect(() => { if (auth) fetchData(); }, [auth, activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStatusChange = async (id, newStatus, type) => {
    try {
      await axios.patch(`${API}/admin/${type}/${id}/status`, { status: newStatus }, authHeaders);
      fetchData();
    } catch (err) { console.error(err); }
  };

  const handleLogin = (tokenData) => {
    localStorage.setItem('nordivca_admin_token', tokenData.token);
    setAuth(tokenData);
  };

  const handleLogout = () => {
    localStorage.removeItem('nordivca_admin_token');
    setAuth(null);
  };

  if (!auth || validating) {
    if (validating) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#00B4DB]" /></div>;
    return <AdminLogin onLogin={handleLogin} />;
  }

  const tabs = [
    { id: 'partners', label: 'Vognmænd', icon: Users },
    { id: 'bookings', label: 'Bookinger', icon: Calendar },
    { id: 'subscriptions', label: 'Medlemskab', icon: CreditCard },
    { id: 'jobs', label: 'Jobansøgere', icon: Briefcase },
    { id: 'inquiries', label: 'Forespørgsler', icon: MessageSquare }
  ];

  const getNewCount = (tabId) => {
    if (!stats) return 0;
    return stats[tabId]?.ny || 0;
  };

  const statusColors = {
    'Ny': 'bg-blue-100 text-blue-800 border-blue-200',
    'I dialog': 'bg-yellow-100 text-yellow-800 border-yellow-200',
    'Godkendt': 'bg-green-100 text-green-800 border-green-200',
    'Booket': 'bg-green-100 text-green-800 border-green-200',
    'Arkiveret': 'bg-slate-100 text-slate-500 border-slate-200'
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <img src={LOGO_URL} alt="NORDIVCA" className="h-10" />
          <h1 className="text-xl font-bold text-slate-900">Admin Dashboard</h1>
        </div>
        <button onClick={handleLogout} className="flex items-center gap-2 text-slate-500 hover:text-slate-900" data-testid="admin-logout-btn">
          <LogOut className="w-5 h-5" /> Log ud
        </button>
      </div>

      <div className="p-6">
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            {[
              { label: 'Vognmænd', value: stats.partners?.total || 0, ny: stats.partners?.ny || 0 },
              { label: 'Bookinger', value: stats.bookings?.total || 0, ny: stats.bookings?.ny || 0 },
              { label: 'Medlemskab', value: stats.subscriptions?.total || 0, ny: stats.subscriptions?.ny || 0 },
              { label: 'Jobansøgere', value: stats.jobs?.total || 0, ny: stats.jobs?.ny || 0 },
              { label: 'Forespørgsler', value: stats.inquiries?.total || 0, ny: stats.inquiries?.ny || 0 }
            ].map((s, i) => (
              <div key={i} className="bg-white rounded-xl p-4 border border-slate-200" data-testid={`stat-card-${i}`}>
                <div className="text-2xl font-bold text-slate-900">{s.value}</div>
                <div className="text-sm text-slate-500">{s.label}</div>
                {s.ny > 0 && <div className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-50 text-red-600 text-xs font-semibold"><span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />{s.ny} nye</div>}
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-2 mb-6 overflow-x-auto">
          {tabs.map((tab) => {
            const newCount = getNewCount(tab.id);
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`relative flex items-center gap-2 px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors
                  ${activeTab === tab.id ? 'bg-[#0083B0] text-white' : 'bg-white text-slate-600 hover:bg-slate-100'}`}
                data-testid={`tab-${tab.id}`}>
                <tab.icon className="w-4 h-4" /> {tab.label}
                {newCount > 0 && (
                  <span className={`ml-1 min-w-[20px] h-5 flex items-center justify-center rounded-full text-xs font-bold ${activeTab === tab.id ? 'bg-white text-[#0083B0]' : 'bg-red-500 text-white'}`}>{newCount}</span>
                )}
              </button>
            );
          })}
        </div>

        <div className="flex justify-end mb-4">
          <a href={`${API}/admin/export/${activeTab}?token=${auth?.token}`} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-2 bg-white border border-slate-200 text-slate-700 px-4 py-2 rounded-lg hover:bg-slate-50" data-testid="export-csv-btn">
            <Download className="w-4 h-4" /> Export CSV
          </a>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
          {isLoading ? (
            <div className="p-12 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto text-[#00B4DB]" /></div>
          ) : data.length === 0 ? (
            <div className="p-12 text-center text-slate-400">Ingen data endnu</div>
          ) : (
            <table className="w-full" data-testid="admin-data-table">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  {activeTab === 'partners' && <><th className="text-left p-4 text-slate-600 text-xs font-semibold uppercase tracking-wider">Navn</th><th className="text-left p-4 text-slate-600 text-xs font-semibold uppercase tracking-wider">CVR</th><th className="text-left p-4 text-slate-600 text-xs font-semibold uppercase tracking-wider">Email</th><th className="text-left p-4 text-slate-600 text-xs font-semibold uppercase tracking-wider">Flåde</th><th className="text-left p-4 text-slate-600 text-xs font-semibold uppercase tracking-wider">Status</th></>}
                  {activeTab === 'bookings' && <><th className="text-left p-4 text-slate-600 text-xs font-semibold uppercase tracking-wider">Navn</th><th className="text-left p-4 text-slate-600 text-xs font-semibold uppercase tracking-wider">Type</th><th className="text-left p-4 text-slate-600 text-xs font-semibold uppercase tracking-wider">Fra</th><th className="text-left p-4 text-slate-600 text-xs font-semibold uppercase tracking-wider">Til</th><th className="text-left p-4 text-slate-600 text-xs font-semibold uppercase tracking-wider">Dato</th><th className="text-left p-4 text-slate-600 text-xs font-semibold uppercase tracking-wider">Status</th></>}
                  {activeTab === 'subscriptions' && <><th className="text-left p-4 text-slate-600 text-xs font-semibold uppercase tracking-wider">Organisation</th><th className="text-left p-4 text-slate-600 text-xs font-semibold uppercase tracking-wider">Kontakt</th><th className="text-left p-4 text-slate-600 text-xs font-semibold uppercase tracking-wider">Tier</th><th className="text-left p-4 text-slate-600 text-xs font-semibold uppercase tracking-wider">Type</th><th className="text-left p-4 text-slate-600 text-xs font-semibold uppercase tracking-wider">Status</th></>}
                  {activeTab === 'jobs' && <><th className="text-left p-4 text-slate-600 text-xs font-semibold uppercase tracking-wider">Navn</th><th className="text-left p-4 text-slate-600 text-xs font-semibold uppercase tracking-wider">Stilling</th><th className="text-left p-4 text-slate-600 text-xs font-semibold uppercase tracking-wider">Email</th><th className="text-left p-4 text-slate-600 text-xs font-semibold uppercase tracking-wider">Filer</th><th className="text-left p-4 text-slate-600 text-xs font-semibold uppercase tracking-wider">Status</th></>}
                  {activeTab === 'inquiries' && <><th className="text-left p-4 text-slate-600 text-xs font-semibold uppercase tracking-wider">Navn</th><th className="text-left p-4 text-slate-600 text-xs font-semibold uppercase tracking-wider">Email</th><th className="text-left p-4 text-slate-600 text-xs font-semibold uppercase tracking-wider">Besked</th><th className="text-left p-4 text-slate-600 text-xs font-semibold uppercase tracking-wider">Status</th></>}
                </tr>
              </thead>
              <tbody>
                {data.map((item) => (
                  <tr key={item.id} className="border-b border-slate-100 hover:bg-slate-50">
                    {activeTab === 'partners' && <>
                      <td className="p-4 font-medium text-slate-900">{item.full_name}</td>
                      <td className="p-4 text-slate-600">{item.cvr_number}</td>
                      <td className="p-4 text-[#0083B0]">{item.email}</td>
                      <td className="p-4 text-slate-600">{item.fleet_size}</td>
                      <td className="p-4">
                        <select value={item.status} onChange={(e) => handleStatusChange(item.id, e.target.value, 'partners')}
                          className={`border rounded-lg px-3 py-1.5 text-sm font-medium cursor-pointer ${statusColors[item.status] || 'border-slate-200'}`}
                          data-testid={`status-select-${item.id}`}>
                          <option value="Ny">Ny</option><option value="I dialog">I dialog</option><option value="Godkendt">Godkendt</option><option value="Arkiveret">Arkiveret</option>
                        </select>
                      </td>
                    </>}
                    {activeTab === 'bookings' && <>
                      <td className="p-4 font-medium text-slate-900">{item.full_name}</td>
                      <td className="p-4 text-slate-600">{item.transport_type}</td>
                      <td className="p-4 text-sm text-slate-600">{item.from_address?.slice(0,30)}</td>
                      <td className="p-4 text-sm text-slate-600">{item.to_address?.slice(0,30)}</td>
                      <td className="p-4 text-slate-600">{item.date}</td>
                      <td className="p-4">
                        <select value={item.status} onChange={(e) => handleStatusChange(item.id, e.target.value, 'bookings')}
                          className={`border rounded-lg px-3 py-1.5 text-sm font-medium cursor-pointer ${statusColors[item.status] || 'border-slate-200'}`}
                          data-testid={`status-select-${item.id}`}>
                          <option value="Ny">Ny</option><option value="I dialog">I dialog</option><option value="Booket">Booket</option><option value="Arkiveret">Arkiveret</option>
                        </select>
                      </td>
                    </>}
                    {activeTab === 'subscriptions' && <>
                      <td className="p-4 font-medium text-slate-900">{item.organization_name}</td>
                      <td className="p-4 text-slate-600">{item.contact_name}</td>
                      <td className="p-4"><span className="px-2 py-1 bg-[#00B4DB]/10 text-[#0083B0] rounded-lg text-sm font-medium">{item.tier}</span></td>
                      <td className="p-4 text-slate-600">{item.organization_type}</td>
                      <td className="p-4">
                        <select value={item.status} onChange={(e) => handleStatusChange(item.id, e.target.value, 'subscriptions')}
                          className={`border rounded-lg px-3 py-1.5 text-sm font-medium cursor-pointer ${statusColors[item.status] || 'border-slate-200'}`}
                          data-testid={`status-select-${item.id}`}>
                          <option value="Ny">Ny</option><option value="I dialog">I dialog</option><option value="Godkendt">Godkendt</option><option value="Arkiveret">Arkiveret</option>
                        </select>
                      </td>
                    </>}
                    {activeTab === 'jobs' && <>
                      <td className="p-4 font-medium text-slate-900">{item.full_name}</td>
                      <td className="p-4 text-slate-600">{item.position}</td>
                      <td className="p-4 text-[#0083B0]">{item.email}</td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          {item.cv_filename ? (
                            <a href={`${API}/admin/jobs/${item.id}/file/cv?token=${auth?.token}`} target="_blank" rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs font-medium hover:bg-blue-100" data-testid={`cv-link-${item.id}`}><FileText className="w-3 h-3" /> CV</a>
                          ) : null}
                          {item.cover_letter_filename ? (
                            <a href={`${API}/admin/jobs/${item.id}/file/cover_letter?token=${auth?.token}`} target="_blank" rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 px-2 py-1 bg-purple-50 text-purple-700 rounded text-xs font-medium hover:bg-purple-100"><FileText className="w-3 h-3" /> Ansøgning</a>
                          ) : null}
                          {item.documents_filename ? (
                            <a href={`${API}/admin/jobs/${item.id}/file/documents?token=${auth?.token}`} target="_blank" rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 px-2 py-1 bg-amber-50 text-amber-700 rounded text-xs font-medium hover:bg-amber-100"><FileText className="w-3 h-3" /> Dok</a>
                          ) : null}
                          {!item.cv_filename && !item.cover_letter_filename && !item.documents_filename && <span className="text-slate-400 text-sm">-</span>}
                        </div>
                      </td>
                      <td className="p-4">
                        <select value={item.status} onChange={(e) => handleStatusChange(item.id, e.target.value, 'jobs')}
                          className={`border rounded-lg px-3 py-1.5 text-sm font-medium cursor-pointer ${statusColors[item.status] || 'border-slate-200'}`}
                          data-testid={`status-select-${item.id}`}>
                          <option value="Ny">Ny</option><option value="I dialog">I dialog</option><option value="Godkendt">Godkendt</option><option value="Arkiveret">Arkiveret</option>
                        </select>
                      </td>
                    </>}
                    {activeTab === 'inquiries' && <>
                      <td className="p-4 font-medium text-slate-900">{item.full_name}</td>
                      <td className="p-4 text-[#0083B0]">{item.email}</td>
                      <td className="p-4 text-sm max-w-xs truncate text-slate-600">{item.message}</td>
                      <td className="p-4">
                        <select value={item.status} onChange={(e) => handleStatusChange(item.id, e.target.value, 'inquiries')}
                          className={`border rounded-lg px-3 py-1.5 text-sm font-medium cursor-pointer ${statusColors[item.status] || 'border-slate-200'}`}
                          data-testid={`status-select-${item.id}`}>
                          <option value="Ny">Ny</option><option value="I dialog">I dialog</option><option value="Godkendt">Godkendt</option><option value="Arkiveret">Arkiveret</option>
                        </select>
                      </td>
                    </>}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

const AdminLogin = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/admin/login`, { username, password });
      onLogin(res.data);
    } catch (err) {
      setError('Forkert brugernavn eller adgangskode');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-blue-50" data-testid="admin-login">
      <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-xl w-full max-w-md">
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="NORDIVCA" className="h-14 mx-auto mb-6" />
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Admin Login</h1>
          <p className="text-slate-500 text-sm mt-2">Log ind for at administrere NORDIVCA</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="form-label">Brugernavn</label>
            <input type="text" placeholder="Brugernavn" className="form-input" value={username} onChange={(e) => setUsername(e.target.value)} data-testid="admin-username" />
          </div>
          <div>
            <label className="form-label">Adgangskode</label>
            <input type="password" placeholder="Adgangskode" className="form-input" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password" />
          </div>
          {error && <p className="text-red-500 text-sm text-center">{error}</p>}
          <button type="submit" disabled={isLoading} className="btn-primary w-full" data-testid="admin-login-button" style={{ backgroundColor: '#5DB0D3' }}>
            {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Log ind'}
          </button>
        </form>
      </div>
    </div>
  );
};

// Layout
const PageLayout = ({ children }) => (
  <>
    <Navigation />
    {children}
    <Footer />
  </>
);

// Main App
function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<PageLayout><HomePage /></PageLayout>} />
          <Route path="/book-transport" element={<PageLayout><BookTransportPage /></PageLayout>} />
          <Route path="/medlemskab" element={<PageLayout><MedlemskabPage /></PageLayout>} />
          <Route path="/for-vognmaend" element={<PageLayout><ForVognmaendPage /></PageLayout>} />
          <Route path="/karriere" element={<PageLayout><KarrierePage /></PageLayout>} />
          <Route path="/om-nordivca" element={<PageLayout><OmNordivcaPage /></PageLayout>} />
          <Route path="/kontakt" element={<PageLayout><KontaktPage /></PageLayout>} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/admin-portal" element={<AdminPage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
